import React, { useState, useEffect } from "react";
import {
  ArrowLeft, Clock, FileText, MessageSquare, Paperclip,
  Upload, CheckCircle, AlertCircle, HelpCircle, ExternalLink, Shield
} from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { detectLocale } from "../i18n";

const wtData = {
  en: {
    title: "Warranty request tracking",
    not_found: "Request not found. Check the URL or contact support.",
    protocol: "Protocol number",
    status: "Current status",
    timeline: "Timeline",
    messages: "Messages",
    documents: "Pending documents",
    eta: "Estimated response",
    updated: "Last update",
    status_opts: ["Received", "Under review", "Awaiting documents", "Technical evaluation", "Approved", "Rejected", "Completed", "Cancelled"],
    add_doc: "Send additional document",
    reply: "Reply to ticket",
    talk: "Contact support",
    support_msg: "If any information is missing, you can supplement the request from this screen.",
    back: "Back to request",
    back_support: "Back to support",
    explore_faq: "Explore FAQ",
    no_docs: "No pending documents",
    no_messages: "No messages yet",
    submit_doc: "Submit document",
    cancel: "Cancel",
    doc_sent: "Document sent successfully",
    message_placeholder: "Type your message...",
    send: "Send",
  },
  pt: {
    title: "Acompanhamento de solicitação de garantia",
    not_found: "Solicitação não encontrada. Verifique a URL ou entre em contato com o suporte.",
    protocol: "Número do protocolo",
    status: "Status atual",
    timeline: "Linha do tempo",
    messages: "Mensagens",
    documents: "Documentos pendentes",
    eta: "Prazo estimado",
    updated: "Última atualização",
    status_opts: ["Recebido", "Em análise", "Aguardando documentos", "Em avaliação técnica", "Aprovado", "Rejeitado", "Concluído", "Cancelado"],
    add_doc: "Enviar documento adicional",
    reply: "Responder ao chamado",
    talk: "Falar com suporte",
    support_msg: "Se faltar alguma informação, você poderá complementar o chamado por esta tela.",
    back: "Voltar para solicitação",
    back_support: "Voltar para suporte",
    explore_faq: "Explorar FAQ",
    no_docs: "Nenhum documento pendente",
    no_messages: "Nenhuma mensagem ainda",
    submit_doc: "Enviar documento",
    cancel: "Cancelar",
    doc_sent: "Documento enviado com sucesso",
    message_placeholder: "Digite sua mensagem...",
    send: "Enviar",
  },
  es: {
    title: "Seguimiento de solicitud de garantía",
    not_found: "Solicitud no encontrada. Verifique la URL o contacte soporte.",
    protocol: "Número de protocolo",
    status: "Estado actual",
    timeline: "Línea de tiempo",
    messages: "Mensajes",
    documents: "Documentos pendientes",
    eta: "Plazo estimado",
    updated: "Última actualización",
    status_opts: ["Recibido", "En análisis", "Esperando documentos", "Evaluación técnica", "Aprobado", "Rechazado", "Completado", "Cancelado"],
    add_doc: "Enviar documento adicional",
    reply: "Responder al ticket",
    talk: "Hablar con soporte",
    support_msg: "Si falta alguna información, puede complementar la solicitud desde esta pantalla.",
    back: "Volver a la solicitud",
    back_support: "Volver a soporte",
    explore_faq: "Explorar FAQ",
    no_docs: "Sin documentos pendientes",
    no_messages: "Sin mensajes aún",
    submit_doc: "Enviar documento",
    cancel: "Cancelar",
    doc_sent: "Documento enviado exitosamente",
    message_placeholder: "Escriba su mensaje...",
    send: "Enviar",
  },
};

const statusColors = {
  0: "bg-sky-500",       // Received
  1: "bg-blue-500",       // Under review
  2: "bg-amber-500",      // Awaiting documents
  3: "bg-purple-500",     // Technical evaluation
  4: "bg-emerald-500",    // Approved
  5: "bg-red-500",        // Rejected
  6: "bg-slate-500",      // Completed
  7: "bg-slate-400",      // Cancelled
};

export default function WarrantyTracking() {
  const { id } = useParams();
  const locale = detectLocale();
  const t = wtData[locale] || wtData.en;

  const [request, setRequest] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("timeline");
  const [showDocForm, setShowDocForm] = useState(false);
  const [docUploaded, setDocUploaded] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const requests = JSON.parse(localStorage.getItem("sharkx_warranty_requests") || "[]");
    const found = requests.find((r) => r.protocol === id);
    if (found) {
      setRequest(found);
      if (found.messages) setMessages(found.messages);
    } else {
      setNotFound(true);
    }
  }, [id]);

  if (notFound) {
    return (
      <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen">
        <div className="max-w-[720px] mx-auto px-6 md:px-12">
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <AlertCircle className="mx-auto text-slate-300 mb-4" size={48} />
            <h1 className="font-serif-display text-2xl text-slate-900 mb-3">{t.not_found}</h1>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link to="/warranty/request"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                <Shield size={16} /> {t.back}
              </Link>
              <Link to="/support"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                <ArrowLeft size={16} /> {t.back_support}
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!request) return null;

  const statusIdx = request.status || 0;
  const statusLabel = t.status_opts[statusIdx] || t.status_opts[0];
  const statusColor = statusColors[statusIdx] || statusColors[0];
  const timeline = request.timeline || [];

  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    const newMsg = {
      id: Date.now(),
      from: "user",
      text: messageText,
      date: new Date().toISOString(),
    };
    const updated = [...messages, newMsg];
    setMessages(updated);
    setMessageText("");
    // Persist
    const requests = JSON.parse(localStorage.getItem("sharkx_warranty_requests") || "[]");
    const idx = requests.findIndex((r) => r.protocol === id);
    if (idx >= 0) {
      requests[idx].messages = updated;
      localStorage.setItem("sharkx_warranty_requests", JSON.stringify(requests));
    }
  };

  return (
    <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen" data-testid="warranty-tracking-page">
      <div className="max-w-[720px] mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-xs font-ui uppercase tracking-[0.15em] text-slate-500 mb-1">{t.protocol}</p>
              <p className="text-lg font-mono font-bold text-slate-900">{request.protocol}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${statusColor}`} />
              <span className="text-sm font-medium text-slate-700">{statusLabel}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-500 mb-1">{t.status}</p>
              <p className="text-sm font-medium text-slate-700">{statusLabel}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">{t.eta}</p>
              <p className="text-sm font-medium text-slate-700">
                {locale === "pt" ? "2-5 dias úteis" : locale === "es" ? "2-5 días hábiles" : "2-5 business days"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">{t.updated}</p>
              <p className="text-sm font-medium text-slate-700">
                {new Date(request.createdAt).toLocaleDateString(locale === "pt" ? "pt-BR" : locale === "es" ? "es-ES" : "en-US")}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white border border-slate-200 rounded-lg p-1">
          {[
            { key: "timeline", label: t.timeline, icon: Clock },
            { key: "messages", label: t.messages, icon: MessageSquare },
            { key: "documents", label: t.documents, icon: FileText },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-sky-500 text-white"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <tab.icon size={14} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8 mb-6">
          {/* Timeline */}
          {activeTab === "timeline" && (
            <div className="space-y-0">
              {timeline.map((event, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${statusColors[event.status] || "bg-slate-300"} flex-shrink-0 mt-1`} />
                    {i < timeline.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
                  </div>
                  <div className="pb-6">
                    <p className="text-sm font-medium text-slate-700">{t.status_opts[event.status] || event.text}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(event.date).toLocaleDateString(locale === "pt" ? "pt-BR" : locale === "es" ? "es-ES" : "en-US", {
                        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Messages */}
          {activeTab === "messages" && (
            <div>
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="mx-auto text-slate-200 mb-3" size={32} />
                  <p className="text-sm text-slate-400">{t.no_messages}</p>
                </div>
              ) : (
                <div className="space-y-4 mb-6">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                        msg.from === "user" ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-700"
                      }`}>
                        <p className="text-sm">{msg.text}</p>
                        <p className={`text-[10px] mt-1 ${msg.from === "user" ? "text-sky-100" : "text-slate-400"}`}>
                          {new Date(msg.date).toLocaleDateString(locale === "pt" ? "pt-BR" : locale === "es" ? "es-ES" : "en-US", {
                            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Message input */}
              <div className="flex gap-2 pt-4 border-t border-slate-100">
                <input
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  placeholder={t.message_placeholder}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSendMessage(); }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageText.trim()}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    messageText.trim()
                      ? "bg-sky-500 hover:bg-sky-600 text-white"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {t.send}
                </button>
              </div>
            </div>
          )}

          {/* Documents */}
          {activeTab === "documents" && (
            <div>
              {docUploaded && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                  <CheckCircle className="text-emerald-500" size={16} />
                  <span className="text-sm text-emerald-700">{t.doc_sent}</span>
                </div>
              )}

              {showDocForm ? (
                <div className="border border-slate-200 rounded-lg p-4 mb-4">
                  <div
                    className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-sky-400 transition-colors"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.multiple = true;
                      input.accept = "image/*,.pdf";
                      input.onchange = () => {
                        setDocUploaded(true);
                        setShowDocForm(false);
                      };
                      input.click();
                    }}
                  >
                    <Upload className="mx-auto text-slate-300 mb-2" size={24} />
                    <p className="text-sm text-slate-500">
                      {locale === "pt" ? "Clique para selecionar arquivos" : locale === "es" ? "Haga clic para seleccionar archivos" : "Click to select files"}
                    </p>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setShowDocForm(false)}
                      className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50"
                    >
                      {t.cancel}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowDocForm(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-dashed border-slate-300 text-sm text-slate-500 hover:border-sky-400 hover:text-sky-600 transition-colors mb-4"
                >
                  <Plus size={16} /> {t.add_doc}
                </button>
              )}

              {request.uploads && Object.keys(request.uploads).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(request.uploads).map(([cat, files]) =>
                    files.map((file, i) => (
                      <div key={`${cat}-${i}`} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <Paperclip size={14} className="text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-700 flex-1 truncate">{file.name}</span>
                        <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">{t.no_docs}</p>
              )}
            </div>
          )}
        </div>

        {/* Support message */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <HelpCircle className="text-slate-400 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-slate-500">{t.support_msg}</p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/warranty/request"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
            <Shield size={16} /> {t.back}
          </Link>
          <Link to="/support"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
            <ArrowLeft size={16} /> {t.back_support}
          </Link>
          <Link to="/faq"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
            <ExternalLink size={16} /> {t.explore_faq}
          </Link>
        </div>
      </div>
    </main>
  );
}
